import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Card, Spin, Row, Col, Statistic, Button, Modal, Form, Input, InputNumber, Select, Tag, Space, Popconfirm, message, DatePicker, Radio, Descriptions } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined, RobotOutlined } from '@ant-design/icons';
import { yuan } from '../utils/format.js';
import { apiFetch, onDataChanged } from '../utils/api.js';
import RpaSyncPanel from '../components/RpaSyncPanel.jsx';
import dayjs from 'dayjs';

const TYPE_COLOR = { '拦截退款': 'red', '签收退款': 'volcano', '部分退款': 'orange', '换货补发': 'blue', '补发': 'cyan' };
const COMP_STATUS_COLOR = { '未申诉': 'default', '申诉中': 'orange', '申诉失败': 'red', '已追回': 'green' };
const COMP_STATUS_OPTIONS = ['未申诉', '申诉中', '申诉失败', '已追回'];
const RF_STATUS_COLOR = { '未申诉': 'default', '正在申诉': 'processing', '申诉失败': 'red', '申诉追回': 'green' };
const RF_STATUS_OPTIONS = ['未申诉', '正在申诉', '申诉失败', '申诉追回'];
const STATUS_COLOR = { '待处理': 'orange', '处理中': 'blue', '已完成': 'green', '已关闭': 'default' };
const RETURN_COLOR = { '不退货': 'default', '待退货': 'orange', '已寄回': 'blue', '已入库': 'green' };
// 渠道标记（复用 financeCalc.orderChannel 的 pdd/wechat/taobao 枚举）
const CHANNEL_LABEL = { 'pdd': '拼多多', 'wechat': '微信', 'taobao': '淘宝' };
const CHANNEL_COLOR = { 'pdd': 'geekblue', 'wechat': 'green', 'taobao': 'magenta' };

const TYPE_OPTIONS = [
  { value: '拦截退款', label: '拦截退款（发货后未收到）' },
  { value: '签收退款', label: '签收退款（已收到）' },
  { value: '部分退款', label: '部分退款（不满意/仅退款）' },
  { value: '换货补发', label: '换货补发（补发成本+运费+少退）' },
  { value: '补发', label: '补发（少数量补发，额外支出产品+运费）' },
];

// 年月日筛选选项（中文展示）
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => String(dayjs().year() - i));
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}月` }));
const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}日` }));

export default function AfterSales() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, refund_total: 0, freight_loss_total: 0, compensation_total: 0, pending: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ keyword: '', type: 'all', status: 'all', channel: 'all' });
  const [year, setYear] = useState(String(dayjs().year()));   // 登记时间：年（默认当前年）
  const [month, setMonth] = useState(String(dayjs().month() + 1)); // 登记时间：月（默认当前月）
  const [day, setDay] = useState('');                          // 登记时间：日（空=整月）
  const [editModal, setEditModal] = useState(null); // null | {} | {record}
  const [detailRecord, setDetailRecord] = useState(null); // 订单编码点击 → 只读详情弹窗
  const [form] = Form.useForm();
  const [orderOptions, setOrderOptions] = useState([]);
  // 补发预览：自动算的重量+运费（前端实时展示）
  const [suppPreview, setSuppPreview] = useState({ weight_kg: 0, freight: 0, master_missing: false });
  const lookupTimer = useRef(null);
  const typeVal = Form.useWatch('type', form);
  const suppQtyVal = Form.useWatch('supplement_quantity', form);
  const suppOrderVal = Form.useWatch('platform_order_no', form);
  const [rpaOpen, setRpaOpen] = useState(false);
  let isAdmin = false;
  try { const u = JSON.parse(localStorage.getItem('admin_user') || '{}'); isAdmin = u.role === 'admin'; } catch {}


  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, pageSize, ...filters });
    if (year) params.set('year', year);
    if (month) params.set('month', month);
    if (day) params.set('day', day);
    apiFetch(`/api/after-sales?${params}`)
      .then(r => r.json())
      .then(d => { setData(d.data); setTotal(d.total); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page, pageSize, filters, year, month, day]);

  const loadStats = useCallback(() => {
    const params = new URLSearchParams();
    if (year) params.set('year', year);
    if (month) params.set('month', month);
    if (day) params.set('day', day);
    apiFetch(`/api/after-sales/stats?${params}`).then(r => r.json()).then(setStats).catch(() => {});
  }, [year, month, day]);

  useEffect(() => { loadData(); loadStats(); }, [loadData, loadStats]);
  useEffect(() => onDataChanged(() => { loadData(); loadStats(); }), [loadData, loadStats]); // 数据变更静默刷新
  // 换货补发：补发数量默认=订单数量（留空时自动填充，可手动改）
  useEffect(() => {
    if (typeVal !== '换货补发') return;
    const qty = form.getFieldValue('product_quantity');
    if (qty && !form.getFieldValue('supplement_quantity')) {
      form.setFieldValue('supplement_quantity', qty);
    }
  }, [typeVal]);
  // 补发类型：监听订单编码+补发件数变化，实时调接口预览估算重量+运费
  useEffect(() => {
    if (typeVal !== '补发' || !suppOrderVal || !suppQtyVal) {
      setSuppPreview({ weight_kg: 0, freight: 0, master_missing: false });
      return;
    }
    const t = setTimeout(() => {
      apiFetch(`/api/after-sales/preview-supplement?platform_order_no=${encodeURIComponent(suppOrderVal)}&supplement_quantity=${suppQtyVal}`)
        .then(r => r.json())
        .then(d => setSuppPreview({ weight_kg: Number(d.weight_kg) || 0, freight: Number(d.freight) || 0, master_missing: !!d.master_missing }))
        .catch(() => setSuppPreview({ weight_kg: 0, freight: 0, master_missing: false }));
    }, 300);
    return () => clearTimeout(t);
  }, [typeVal, suppOrderVal, suppQtyVal]);

  const doLookup = (kw) => {
    clearTimeout(lookupTimer.current);
    if (!kw || kw.length < 2) { setOrderOptions([]); return; }
    lookupTimer.current = setTimeout(() => {
      apiFetch(`/api/after-sales/order-lookup?keyword=${encodeURIComponent(kw)}`)
        .then(r => r.json())
        .then(d => setOrderOptions(d.data || []))
        .catch(() => {});
    }, 300);
  };

  const pickOrder = (orderNo) => {
    const o = orderOptions.find(x => x.platform_order_no === orderNo);
    if (!o) return;
    form.setFieldsValue({
      shop: o.shop, product_name: o.product_name, spec_code: o.spec_code,
      product_quantity: o.product_quantity, order_price: o.order_price,
      tracking_no: o.tracking_no, courier_company: o.courier_company,
    });
  };

  const openAdd = () => {
    form.resetFields();
    form.setFieldsValue({ register_time: dayjs(), type: '拦截退款', status: '待处理', return_warehouse: '否', return_status: '不退货', out_freight: 0, return_freight: 0, return_freight_status: '未申诉', compensation_status: '未申诉' });
    setOrderOptions([]);
    setEditModal({});
  };
  const openEdit = (record) => {
    form.resetFields();
    form.setFieldsValue({
      ...record,
      register_time: record.register_time ? dayjs(record.register_time) : dayjs(),
    });
    setOrderOptions([{ platform_order_no: record.platform_order_no, shop: record.shop, product_name: record.product_name, spec_code: record.spec_code, product_quantity: record.product_quantity, order_price: record.order_price, tracking_no: record.tracking_no, courier_company: record.courier_company }]);
    setEditModal(record);
  };

  const doSave = async (payload) => {
    try {
      let res;
      if (editModal?.id) {
        res = await apiFetch(`/api/after-sales/${editModal.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        }).then(r => r.json());
      } else {
        res = await apiFetch('/api/after-sales', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        }).then(r => r.json());
      }
      if (res.success) { message.success('已保存售后单'); setEditModal(null); loadData(); loadStats(); }
      else message.error(res.error || '保存失败');
    } catch (e) {
      if (e?.errorFields) return;
      message.error('保存失败，请重试');
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      register_time: values.register_time ? values.register_time.format('YYYY-MM-DD HH:mm') : '',
    };
    // 编辑售后单时：规格编码被修改 → 弹确认（发错货场景，退货会退到新编码库存）
    const oldCode = editModal?.id ? String(editModal.spec_code || '').trim() : '';
    const newCode = String(payload.spec_code || '').trim();
    if (editModal?.id && oldCode && newCode && oldCode !== newCode) {
      Modal.confirm({
        title: '确认修改规格编码？',
        content: `退货入库目标将变更：\n原编码：${oldCode}\n新编码：${newCode}\n\n若属「发错货、退回的是另一种商品」，请确认；确认后退货库存将退到「${newCode}」对应的库存。`,
        okText: '确认修改',
        cancelText: '取消',
        onOk: () => doSave(payload),
      });
      return;
    }
    doSave(payload);
  };

  const handleDelete = async (id) => {
    const res = await apiFetch(`/api/after-sales/${id}`, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除'); loadData(); loadStats(); }
    else message.error(res.error || '删除失败');
  };

  const columns = [
    { title: '订单编码', dataIndex: 'platform_order_no', width: 200, fixed: 'left', ellipsis: true,
      render: (v, r) => (
        <a style={{ color: 'var(--brand)', fontWeight: 500, cursor: 'pointer' }} onClick={() => setDetailRecord(r)}>{v}</a>
      ) },
    { title: '登记时间', dataIndex: 'register_time', width: 140 },
    {
      title: '商品信息', key: 'product', width: 220,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.product_name}</div>
          <div style={{ color: '#8c8c8c', fontSize: 12 }}>{r.spec_code} · 数量 {r.product_quantity}</div>
        </div>
      ),
    },
    { title: '商品价格', dataIndex: 'order_price', width: 90, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '发货单号', dataIndex: 'tracking_no', width: 150, ellipsis: true },
    {
      title: '售后类型', dataIndex: 'type', width: 100,
      render: v => <Tag color={TYPE_COLOR[v] || 'default'}>{v}</Tag>,
    },
    { title: '退款金额', dataIndex: 'refund_amount', width: 95, align: 'right',
      render: (v, r) => (r.type === '换货补发' || r.type === '补发') ? <span style={{ color: '#bfbfbf' }}>—</span> : <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan(r.type === '签收退款' ? r.return_amount : v)}</span> },
    {
      title: '售后补偿金', key: 'compensation', width: 130, align: 'center',
      render: (_, r) => {
        if (!Number(r.compensation)) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <Space size={4} wrap style={{ rowGap: 2 }}>
            <span style={{ color: '#EF9F27', fontWeight: 600 }}>¥{yuan(r.compensation)}</span>
            <Tag color={COMP_STATUS_COLOR[r.compensation_status] || 'default'} style={{ marginInlineEnd: 0 }}>
              {r.compensation_status || '未申诉'}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: '运费损失', key: 'return_freight', width: 130,
      render: (_, r) => {
        const rf = Number(r.return_freight) || 0;
        if (!rf) return <span style={{ color: '#bfbfbf', textAlign: 'right', display: 'block' }}>—</span>;
        const recovered = String(r.return_freight_status || '未申诉') === '申诉追回';
        return (
          <Space size={4} wrap style={{ rowGap: 2, justifyContent: 'flex-end', width: '100%' }}>
            <span style={{ color: recovered ? '#3F8600' : '#CF1322', fontWeight: 600 }}>
              {recovered ? '¥0.00（已追回）' : `¥${yuan(rf)}`}
            </span>
            <Tag color={RF_STATUS_COLOR[r.return_freight_status] || 'default'} style={{ marginInlineEnd: 0 }}>
              {r.return_freight_status || '未申诉'}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: '补发/换货损失', key: 'supplement', width: 240,
      render: (_, r) => {
        if (r.type === '补发') {
          const lines = [];
          lines.push(`补发 ${r.supplement_quantity}件${Number(r.supplement_weight_kg) ? '·' + r.supplement_weight_kg + 'kg' : ''} · 成本 ¥${yuan(r.supplement_cost)} · 运费 ¥${yuan(r.supplement_freight)}`);
          if (r.supplement_tracking_no) lines.push(`单号：${r.supplement_tracking_no}`);
          if (r.supplement_master_missing) lines.push('⚠️ 未登记编码，运费手填');
          const text = lines.join('\n');
          return <span style={{ color: '#CF1322', whiteSpace: 'pre-line' }} title={text}>{text}</span>;
        }
        if (r.type === '换货补发') {
          const parts = [];
          if (Number(r.supplement_quantity)) parts.push(`补发 ${r.supplement_quantity}件`);
          if (Number(r.supplement_cost)) parts.push(`成本 ¥${yuan(r.supplement_cost)}`);
          if (Number(r.missing_goods_cost)) parts.push(`少退损失 ¥${yuan(r.missing_goods_cost)}`);
          if (!parts.length) return <span style={{ color: '#bfbfbf' }}>—</span>;
          const text = parts.join(' · ');
          return <span style={{ color: '#CF1322' }} title={text}>{text}</span>;
        }
        return <span style={{ color: '#bfbfbf' }}>—</span>;
      },
    },
    { title: '售后原因', dataIndex: 'reason', width: 180, ellipsis: true },
    { title: '处理状态', dataIndex: 'status', width: 90, render: v => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag> },
    { title: '退货入库', dataIndex: 'return_warehouse', width: 90, align: 'center' },
    { title: '退货数量', dataIndex: 'return_quantity', width: 80, align: 'right' },
    { title: '退货状态', dataIndex: 'return_status', width: 90, render: v => <Tag color={RETURN_COLOR[v] || 'default'}>{v}</Tag> },
    { title: '退回单号', dataIndex: 'return_tracking_no', width: 150, ellipsis: true,
      render: (v) => v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : <span style={{ color: '#bfbfbf' }}>—</span> },
    { title: '渠道', dataIndex: 'channel', width: 80, align: 'center',
      render: v => v && CHANNEL_LABEL[v] ? <Tag color={CHANNEL_COLOR[v]}>{CHANNEL_LABEL[v]}</Tag> : <span style={{ color: '#bfbfbf' }}>—</span> },
    { title: '店铺名称', dataIndex: 'shop', width: 120, ellipsis: true },
    {
      title: '操作', key: 'op', width: 130, fixed: 'right',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确认删除该售后单？" onConfirm={() => handleDelete(r.id)} okText="删除" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && !data.length) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>售后管理</div>
          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>手动登记售后单，通过订单编码与订单管理/商品成本等页联动</div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { loadData(); loadStats(); }}>刷新</Button>
          {isAdmin && <Button icon={<RobotOutlined />} onClick={() => setRpaOpen(true)}>拼多多同步</Button>}
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>登记售后单</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': 'var(--brand)', padding: '16px 18px' }}>
            <div className="stat-label">售后单总数</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{stats.total}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>单</span></div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': '#CF1322', padding: '16px 18px' }}>
            <div className="stat-label">退款总额</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#CF1322' }}>¥{yuan(stats.refund_total)}</div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': '#EF9F27', padding: '16px 18px' }}>
            <div className="stat-label">售后补偿金</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#EF9F27' }}>¥{yuan(stats.compensation_total)}</div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': '#7F77DD', padding: '16px 18px' }}>
            <div className="stat-label">运费损失（已追回不计）</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#7F77DD' }}>¥{yuan(stats.freight_loss_total)}</div>
          </div>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <span style={{ color: '#8c8c8c' }}>登记时间：</span>
          <Select style={{ width: 110 }} placeholder="全部年份" value={year || undefined} allowClear
            onChange={v => { setYear(v || ''); setMonth(''); setDay(''); setPage(1); }}
            options={YEAR_OPTIONS.map(y => ({ value: y, label: `${y}年` }))} />
          <Select style={{ width: 100 }} placeholder="月" value={month || undefined} allowClear disabled={!year}
            onChange={v => { setMonth(v || ''); setDay(''); setPage(1); }}
            options={MONTH_OPTIONS} />
          <Select style={{ width: 90 }} placeholder="整月" value={day || undefined} allowClear disabled={!year || !month}
            onChange={v => { setDay(v || ''); setPage(1); }}
            options={DAY_OPTIONS} />
          <Input.Search
            placeholder="搜索订单编码/商品/单号/店铺"
            allowClear style={{ width: 300 }}
            value={filters.keyword}
            onChange={e => setFilters({ ...filters, keyword: e.target.value })}
            onSearch={() => { setPage(1); loadData(); }}
            prefix={<SearchOutlined />}
          />
          <Select style={{ width: 160 }} value={filters.type} onChange={v => { setFilters({ ...filters, type: v }); setPage(1); }}
            options={[{ value: 'all', label: '全部类型' }, ...TYPE_OPTIONS.map(t => ({ value: t.value, label: t.value }))]} />
          <Select style={{ width: 130 }} value={filters.status} onChange={v => { setFilters({ ...filters, status: v }); setPage(1); }}
            options={[{ value: 'all', label: '全部状态' }, ...['待处理', '处理中', '已完成', '已关闭'].map(s => ({ value: s, label: s }))]} />
          <Select style={{ width: 120 }} value={filters.channel} onChange={v => { setFilters({ ...filters, channel: v }); setPage(1); }}
            options={[{ value: 'all', label: '全部渠道' }, { value: 'pdd', label: '拼多多' }, { value: 'wechat', label: '微信' }, { value: 'taobao', label: '淘宝' }]} />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => { setPage(1); loadData(); }}>查询</Button>
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 2150 }}
        pagination={{
          current: page, pageSize, total,
          showSizeChanger: true, showQuickJumper: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
      />

      <Modal
        title={editModal?.id ? `编辑售后单 #${editModal.id}` : '登记售后单'}
        open={!!editModal}
        onCancel={() => setEditModal(null)}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        width={760}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="platform_order_no" label="订单编码" rules={[{ required: true, message: '请选择/输入订单编码' }]}
            extra={<span style={{ color: '#8c8c8c' }}>输入订单号/快递单号/商品关键词搜索订单，选中后自动带出商品、价格、单号、店铺</span>}>
            <Select
              showSearch
              filterOption={false}
              onSearch={doLookup}
              onChange={pickOrder}
              placeholder="搜索并选择订单"
              notFoundContent={null}
              options={orderOptions.map(o => ({
                value: o.platform_order_no,
                label: `${o.platform_order_no}（${o.product_name}）`,
              }))}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="register_time" label="登记时间" rules={[{ required: true }]}>
                <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="shop" label="售后店铺名称">
                <Input placeholder="自动带出" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item name="product_name" label="商品信息">
                <Input placeholder="自动带出" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="spec_code" label="规格编码">
                <Input placeholder="自动带出" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="product_quantity" label="数量">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="order_price" label="商品价格(元)">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tracking_no" label="发货单号">
                <Input placeholder="自动带出" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="type" label="售后类型" rules={[{ required: true }]}>
            <Radio.Group options={TYPE_OPTIONS} />
          </Form.Item>

          <Row gutter={12}>
            {typeVal === '签收退款' ? (
              <>
                <Col span={8}>
                  <Form.Item name="return_freight" label="退回运费(元)" extra={<span style={{ color: '#8c8c8c' }}>消费者退货的物流费</span>}>
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="退回运费" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="return_freight_status" label="运费申诉状态" extra={<span style={{ color: '#8c8c8c' }}>申诉追回则财务加回运费</span>}>
                    <Select style={{ width: '100%' }} options={RF_STATUS_OPTIONS.map(s => ({ value: s, label: s }))} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="return_amount" label="退款金额(元)" extra={<span style={{ color: '#8c8c8c' }}>消费者退回的货款金额</span>}>
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="退款金额" />
                  </Form.Item>
                </Col>
              </>
            ) : typeVal === '换货补发' ? (
              <>
                <Col span={6}>
                  <Form.Item name="out_freight" label="补发出库运费(元)" extra={<span style={{ color: '#8c8c8c' }}>重新发货的物流费</span>}>
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="发出运费" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="return_freight" label="寄回运费(元)" extra={<span style={{ color: '#8c8c8c' }}>消费者寄回的物流费</span>}>
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="寄回运费" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="supplement_quantity" label="补发数量" extra={<span style={{ color: '#8c8c8c' }}>默认=订单数量，可改</span>}>
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="补发数量" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="return_freight_status" label="运费申诉状态" extra={<span style={{ color: '#8c8c8c' }}>申诉追回则加回运费</span>}>
                    <Select style={{ width: '100%' }} options={RF_STATUS_OPTIONS.map(s => ({ value: s, label: s }))} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="supplement_tracking_no" label="补发单号" extra={<span style={{ color: '#8c8c8c' }}>补发走新运单，记录后才能按运单号匹配极兔账单</span>}>
                    <Input placeholder="补发快递单号（选填，账单对账时按运单号匹配）" />
                  </Form.Item>
                </Col>
              </>
            ) : typeVal === '补发' ? (
              <>
                <Col span={8}>
                  <Form.Item name="supplement_quantity" label="补发件数" rules={[{ required: true, message: '请输入补发件数' }]}>
                    <InputNumber min={1} style={{ width: '100%' }} placeholder="补发件数" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="supplement_tracking_no" label="补发单号" rules={[{ required: true, message: '请输入补发快递单号（用于账单对账）' }]}
                    extra={<span style={{ color: '#8c8c8c' }}>补发走新运单，记录后才能按运单号匹配极兔账单</span>}>
                    <Input placeholder="补发快递单号" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="预估重量/运费">
                    <div style={{ paddingTop: 8, lineHeight: 1.6 }}>
                      {suppPreview.weight_kg > 0 ? (
                        <span style={{ color: '#26201C' }}>{suppPreview.weight_kg} kg</span>
                      ) : <span style={{ color: '#bfbfbf' }}>— kg</span>}
                      <span style={{ margin: '0 6px', color: '#bfbfbf' }}>·</span>
                      {suppPreview.freight > 0 ? (
                        <span style={{ color: 'var(--brand)', fontWeight: 600 }}>¥{yuan(suppPreview.freight)}</span>
                      ) : <span style={{ color: '#bfbfbf' }}>¥—</span>}
                    </div>
                  </Form.Item>
                </Col>
                {suppPreview.master_missing && (
                  <Col span={24}>
                    <div style={{ background: '#FFF7E6', border: '1px solid #FFD591', borderRadius: 6, padding: '8px 12', marginBottom: 8 }}>
                      <div style={{ color: '#D46B08', marginBottom: 6 }}>⚠️ 该规格未登记重量信息，自动算的运费为 ¥0，请手动填实际运费</div>
                      <Form.Item name="supplement_freight_manual" label="补发运费(兜底)" style={{ marginBottom: 0 }}>
                        <InputNumber min={0} precision={2} style={{ width: 240 }} placeholder="实际运费" />
                      </Form.Item>
                    </div>
                  </Col>
                )}
              </>
            ) : (
              <Col span={12}>
                <Form.Item name="refund_amount" label="退款金额(元)">
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
                </Form.Item>
              </Col>
            )}
            <Col span={12}>
              <Form.Item name="status" label="售后处理状态">
                <Select options={['待处理', '处理中', '已完成', '已关闭'].map(s => ({ value: s, label: s }))} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="compensation" label="售后补偿金(元)" extra={<span style={{ color: '#8c8c8c' }}>平台误判支出的补偿金，可申诉追回；未申诉/申诉中/申诉失败时财务扣减，已追回则财务加回</span>}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="补偿金额（无则留空）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="compensation_status" label="补偿状态">
                <Select options={COMP_STATUS_OPTIONS.map(s => ({ value: s, label: s }))} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="return_warehouse" label="退货入库">
                <Select options={[{ value: '是', label: '是' }, { value: '否', label: '否' }]} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="return_quantity" label="退货数量">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="return_status" label="退货状态">
                <Select options={['不退货', '待退货', '已寄回', '已入库'].map(s => ({ value: s, label: s }))} />
              </Form.Item>
            </Col>
          </Row>

          {(typeVal === '拦截退款' || typeVal === '签收退款' || typeVal === '换货补发') && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="return_tracking_no" label="退回单号" extra={<span style={{ color: '#8c8c8c' }}>消费者退回商品时的快递单号，用于追踪退回件对应订单</span>}>
                  <Input placeholder="退回快递单号" />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item name="reason" label="售后原因">
            <Input.TextArea rows={2} placeholder="售后原因说明" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<span>售后单详情{detailRecord?.id ? ` #${detailRecord.id}` : ''}</span>}
        open={!!detailRecord}
        onCancel={() => setDetailRecord(null)}
        footer={<Button onClick={() => setDetailRecord(null)}>关闭</Button>}
        width={780}
        destroyOnClose
      >
        {detailRecord && (
          <Descriptions bordered size="small" column={2} style={{ marginTop: 8 }}
            labelStyle={{ width: 130, fontWeight: 500, color: '#8c8c8c', background: '#FAF6F2' }}>
            <Descriptions.Item label="订单编码" span={2}>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{detailRecord.platform_order_no || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="登记时间">{detailRecord.register_time || '—'}</Descriptions.Item>
            <Descriptions.Item label="店铺名称">{detailRecord.shop || '—'}</Descriptions.Item>
            <Descriptions.Item label="售后类型">
              <Tag color={TYPE_COLOR[detailRecord.type] || 'default'}>{detailRecord.type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="处理状态">
              <Tag color={STATUS_COLOR[detailRecord.status] || 'default'}>{detailRecord.status || '—'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="商品名称" span={2} style={{ fontWeight: 500 }}>{detailRecord.product_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="规格编码">
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{detailRecord.spec_code || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="数量">{detailRecord.product_quantity ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="商品价格">¥{yuan(detailRecord.order_price)}</Descriptions.Item>
            <Descriptions.Item label="发货单号">
              {detailRecord.tracking_no ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{detailRecord.tracking_no}</span> : '—'}
            </Descriptions.Item>

            {detailRecord.type !== '换货补发' && detailRecord.type !== '补发' && (
              <Descriptions.Item label="退款金额">
                <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan(detailRecord.type === '签收退款' ? detailRecord.return_amount : detailRecord.refund_amount)}</span>
              </Descriptions.Item>
            )}

            <Descriptions.Item label="售后补偿金">
              {Number(detailRecord.compensation) ? <span style={{ color: '#EF9F27', fontWeight: 600 }}>¥{yuan(detailRecord.compensation)}</span> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="补偿状态">
              <Tag color={COMP_STATUS_COLOR[detailRecord.compensation_status] || 'default'}>{detailRecord.compensation_status || '未申诉'}</Tag>
            </Descriptions.Item>

            <Descriptions.Item label="退回运费">
              {Number(detailRecord.return_freight) ? <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan(detailRecord.return_freight)}</span> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="运费申诉状态">
              <Tag color={RF_STATUS_COLOR[detailRecord.return_freight_status] || 'default'}>{detailRecord.return_freight_status || '未申诉'}</Tag>
            </Descriptions.Item>

            {detailRecord.type === '补发' && (
              <>
                <Descriptions.Item label="补发件数">{detailRecord.supplement_quantity ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="补发重量">{Number(detailRecord.supplement_weight_kg) ? `${detailRecord.supplement_weight_kg} kg` : '—'}</Descriptions.Item>
                <Descriptions.Item label="补发成本">
                  <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan(detailRecord.supplement_cost)}</span>
                </Descriptions.Item>
                <Descriptions.Item label="补发运费">
                  <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan(detailRecord.supplement_freight)}</span>
                </Descriptions.Item>
                <Descriptions.Item label="补发单号" span={2}>
                  {detailRecord.supplement_tracking_no ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{detailRecord.supplement_tracking_no}</span> : '—'}
                </Descriptions.Item>
              </>
            )}

            {detailRecord.type === '换货补发' && (
              <>
                <Descriptions.Item label="补发出库运费">{Number(detailRecord.out_freight) ? `¥${yuan(detailRecord.out_freight)}` : '—'}</Descriptions.Item>
                <Descriptions.Item label="补发数量">{detailRecord.supplement_quantity ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="补发成本">{Number(detailRecord.supplement_cost) ? `¥${yuan(detailRecord.supplement_cost)}` : '—'}</Descriptions.Item>
                <Descriptions.Item label="少退损失">{Number(detailRecord.missing_goods_cost) ? <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan(detailRecord.missing_goods_cost)}</span> : '—'}</Descriptions.Item>
                <Descriptions.Item label="补发单号" span={2}>
                  {detailRecord.supplement_tracking_no ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{detailRecord.supplement_tracking_no}</span> : '—'}
                </Descriptions.Item>
              </>
            )}

            <Descriptions.Item label="退货入库">{detailRecord.return_warehouse || '否'}</Descriptions.Item>
            <Descriptions.Item label="退货数量">{detailRecord.return_quantity ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="退货状态">
              <Tag color={RETURN_COLOR[detailRecord.return_status] || 'default'}>{detailRecord.return_status || '不退货'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="退回单号">
              {detailRecord.return_tracking_no ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{detailRecord.return_tracking_no}</span> : '—'}
            </Descriptions.Item>

            <Descriptions.Item label="售后原因" span={2}>{detailRecord.reason || '—'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <RpaSyncPanel open={rpaOpen} onClose={() => setRpaOpen(false)} />
    </div>
  );
}
